import type { CustomRuleType } from '~/types/loyaltyContract';

/** The identifier kinds a custom rule can be anchored on. */
export type CustomRuleIdentifierType = 'offerId' | 'packageId' | 'earnKey' | 'promoKey' | 'productType';

/**
 * Minimal slice of the create/update form needed to validate required fields
 * client-side before sending a request to svc-lux-loyalty.
 */
export interface CustomRuleFieldInput {
  name?: string;
  startDate?: string;
  type?: CustomRuleType;
  /** Which identifier the user chose to anchor the rule on. */
  selectedIdentifierType: CustomRuleIdentifierType;
  /** The value entered/selected for the chosen identifier. */
  identifierValue?: string | null;
  pointsValue?: number;
  pointsMultiplier?: number;
}

/**
 * Validates the required custom-rule form fields client-side so we never send a
 * request that the server is guaranteed to reject (e.g. empty name or identifier
 * value). Mirrors the fields the form marks `required` and keys errors to the
 * field names the dialogs use for inline display.
 *
 * @param input - The relevant form values.
 * @returns A field-keyed error map; empty when all required fields are present.
 */
export function validateCustomRuleFields(input: CustomRuleFieldInput): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!input.name?.trim()) {
    errors.name = 'Name is required';
  }

  if (!input.startDate) {
    errors.startDate = 'Start date is required';
  }

  // The selected identifier must have a value; key the error to that identifier
  // so the relevant field shows it (offerId / packageId / earnKey / promoKey / productType).
  const identifierValue =
    typeof input.identifierValue === 'string' ? input.identifierValue.trim() : input.identifierValue;
  if (!identifierValue) {
    errors[input.selectedIdentifierType] = `${IDENTIFIER_LABELS[input.selectedIdentifierType]} is required`;
  }

  // Points fields are required per rule type (matching the conditionally-rendered inputs).
  if (input.type === 'BONUS_POINTS' || input.type === 'BONUS_POINTS_ONLY') {
    if (input.pointsValue == null || Number.isNaN(input.pointsValue)) {
      errors.pointsValue = 'Points value is required';
    }
  } else if (input.type === 'MULTIPLE_ON_BASE_POINTS') {
    if (input.pointsMultiplier == null || Number.isNaN(input.pointsMultiplier)) {
      errors.pointsMultiplier = 'Points multiplier is required';
    }
  }

  return errors;
}

/** Human-readable labels for identifier types, used in error messages. */
const IDENTIFIER_LABELS: Record<CustomRuleIdentifierType, string> = {
  offerId: 'Offer ID',
  packageId: 'Package ID',
  earnKey: 'Earn Key',
  promoKey: 'Promo Key',
  productType: 'Product Type',
};
